import { ABNValidator } from "lib/validation";
import { PICValidator } from "lib/validation/validators";

import {
  streetNumberValidation,
  routeValidation,
  localityValidation,
  stateValidation,
  postalCodeValidation,
} from "components/Form/Fields/AddressForm/AddressForm";

import * as Yup from "yup";
import {
  accountNumberValidation,
  bsbValidation,
  accountNameValidation,
} from "components/Form/Fields/BankingDetailsForm/BankingDetailsForm";

interface BusinessValueGetter {
  (business: Business | null): string;
}

export interface FieldConfiguration {
  value: string; // Reference that needs to be unique here.
  targetFieldName: string; // The field name on the business form.
  label: string; // A human readable label for the field.
  validation?: Yup.AnySchema | null; // A Yup Validation function that will be used to validate the field.
  businessValueGetter: BusinessValueGetter; // A function that will be used to get the current value of the field from the business.
  sourceFieldSuggestions: string[]; // A list of field names from a CSV that will trigger this field as a default value in the source data.
  group?: string; // A string value that indicates the group of the field, used for display purposes.
  handleAsProperty?: boolean; // Properties are handled differently to generic fields.
  handleAsContact?: boolean; // So are contacts.
  index?: number; // A number value that indicates the index when used with contacts or pics.
  fieldName?: string; // A string value that indicates the field name on the business form.   Falls back to value in most cases.
}

const UNKNOWN_VALUE = "-";

const emailRecipientsValueGetter: BusinessValueGetter = (
  business: Business | null,
) =>
  business
    ? `${business.emailRecipients.length} existing contacts.`
    : UNKNOWN_VALUE;

const contactConfigurations = [1, 2, 3, 4, 5].reduce((acc, index) => {
  acc[`contact_email_${index}`] = {
    value: `contact_email_${index}`,
    label: `Contact Email ${index}`,
    businessValueGetter: emailRecipientsValueGetter,
    sourceFieldSuggestions: [`email${index}`].concat(
      index === 1 ? ["email"] : [],
    ),
    index,
    fieldName: "email",
    handleAsContact: true,
    group: index === 1 ? "Contacts" : undefined,
    validation: Yup.string().email("Invalid email"),
  };
  acc[`contact_phone_${index}`] = {
    value: `contact_phone_${index}`,
    label: `Contact Phone ${index}`,
    businessValueGetter: emailRecipientsValueGetter,
    sourceFieldSuggestions: [`phone${index}`].concat(
      index === 1 ? ["phone"] : [],
    ),
    index,
    fieldName: "phoneNumber",
    handleAsContact: true,
  };
  acc[`contact_first_name_${index}`] = {
    value: `contact_first_name_${index}`,
    label: `Contact First Name ${index}`,
    businessValueGetter: emailRecipientsValueGetter,
    sourceFieldSuggestions: [`firstname${index}`].concat(
      index === 1 ? ["firstname"] : [],
    ),
    index,
    fieldName: "firstName",
    handleAsContact: true,
  };
  acc[`contact_last_name_${index}`] = {
    value: `contact_last_name_${index}`,
    label: `Contact Last Name ${index}`,
    businessValueGetter: emailRecipientsValueGetter,
    sourceFieldSuggestions: [`lastname${index}`].concat(
      index === 1 ? ["lastname"] : [],
    ),
    index,
    fieldName: "lastName",
    handleAsContact: true,
  };
  return acc;
}, {});

const propertyConfigurations = [1, 2, 3, 4, 5].reduce((acc, index) => {
  acc[`PIC_${index}`] = {
    value: `PIC_${index}`,
    label: `PIC ${index}`,
    businessValueGetter: (business: Business | null) =>
      business
        ? `${business.properties.length} existing properties.`
        : UNKNOWN_VALUE,
    sourceFieldSuggestions: index === 1 ? [`piccode`, "pic"] : [],
    handleAsProperty: true,
    index,
    group: index === 1 ? "Properties" : undefined,
    validation: PICValidator,
  };
  return acc;
}, {});

export const fieldConfigurations: Record<string, FieldConfiguration> = {
  // Simple "General Collapse" fields.
  NAME: {
    value: "name",
    targetFieldName: "name",
    label: "Name",
    validation: null,
    businessValueGetter: (business: Business | null) =>
      business?.name ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: ["name", "displayname", "tradingname"],
    group: "General",
  },
  PUBLIC_DISPLAY_NAME: {
    value: "publicDisplayName",
    targetFieldName: "publicDisplayName",
    label: "Public Display Name",
    validation: null,
    businessValueGetter: (business: Business | null) =>
      business?.publicDisplayName ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: [],
  },
  SHORT_CODE: {
    value: "shortCode",
    targetFieldName: "shortCode",
    label: "Agent Reference",
    validation: null,
    businessValueGetter: (business: Business | null) =>
      business?.shortCode ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: ["agentreference", "code", "agentcode", "agentref"],
  },
  SHORT_CODE_SALEYARD: {
    value: "shortCodeSaleyard",
    targetFieldName: "shortCodeSaleyard",
    label: "Saleyard Reference",
    validation: null,
    businessValueGetter: (business: Business | null) =>
      business?.shortCodeSaleyard ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: [
      "saleyardreference",
      "saleyardcode",
      "syref",
      "sycode",
    ],
  },
  SHORT_CODE_AUCTIONS_PLUS: {
    value: "shortCodeAuctionsPlus",
    targetFieldName: "shortCodeAuctionsPlus",
    label: "Auctions Plus Shortcode",
    validation: null,
    businessValueGetter: (business: Business | null) =>
      business?.shortCodeAuctionsPlus ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: [
      "auctionspshortcode",
      "auctionsplusshortcode",
      "auctionsplusshortcode",
      "auctionsplusshortcode",
    ],
  },
  NOTES: {
    value: "notes",
    targetFieldName: "notes",
    label: "Notes",
    validation: null,
    businessValueGetter: (business: Business | null) =>
      business?.notes ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: ["note", "notes"],
  },
  BRANDS: {
    value: "brands",
    targetFieldName: "brands",
    label: "Brands",
    validation: null,
    businessValueGetter: (business: Business | null) =>
      business?.brands ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: ["brand", "brands"],
  },

  // TODO booleans..
  // Address fields.
  ADDRESSS_STREET_NUMBER: {
    value: "addressStreetNumber",
    label: "Address Street Number",
    targetFieldName: "address.streetNumber",
    validation: streetNumberValidation,
    businessValueGetter: (business: Business | null) =>
      business?.address?.streetNumber?.toString() ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: ["streetnumber", "addressstreetnumber"],
  },
  ADDRESSS_ROUTE: {
    value: "addressRoute",
    label: "Address Street Name",
    targetFieldName: "address.route",
    validation: routeValidation,
    businessValueGetter: (business: Business | null) =>
      business?.address?.route?.toString() ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: [
      "addressroute",
      "streetname",
      "street",
      "address1",
    ],
  },
  ADDRESSS_LOCALITY: {
    value: "addressLocality",
    label: "Address City",
    targetFieldName: "address.locality",
    validation: localityValidation,
    businessValueGetter: (business: Business | null) =>
      business?.address?.locality?.toString() ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: ["locality", "town", "city", "suburb", "address2"],
  },
  ADDRESSS_STATE: {
    value: "addressState",
    label: "Address State",
    targetFieldName: "address.state",
    validation: stateValidation,
    businessValueGetter: (business: Business | null) =>
      business?.address?.state?.toString() ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: ["addressstate", "state", "statename"],
  },
  ADDRESSS_POSTAL_CODE: {
    value: "addressPostalCode",
    label: "Address Post Code",
    targetFieldName: "address.postalCode",
    validation: postalCodeValidation,
    businessValueGetter: (business: Business | null) =>
      business?.address?.postalCode?.toString() ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: ["postcode", "postalcode", "physicalpostcode"],
  },
  // Financial fields.
  BSB: {
    value: "bankingDetailsBsb",
    label: "BSB",
    targetFieldName: "bankingDetails.bsb",
    validation: bsbValidation,
    businessValueGetter: (business: Business | null) =>
      business?.bankingDetails?.bsb ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: ["bsb", "bsbnumber"],
    group: "Financial",
  },
  ACCOUNT_NUMBER: {
    value: "bankingDetailsAccountNumber",
    label: "Account Number",
    targetFieldName: "bankingDetails.accountNumber",
    validation: accountNumberValidation,
    businessValueGetter: (business: Business | null) =>
      business?.bankingDetails?.accountNumber ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: ["accountnumber", "account"],
  },

  ACCOUNT_NAME: {
    value: "bankingDetailsAccountName",
    label: "Account Name",
    targetFieldName: "bankingDetails.accountName",
    validation: accountNameValidation,
    businessValueGetter: (business: Business | null) =>
      business?.bankingDetails?.accountName ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: ["accountname", "accname"],
  },

  ABN: {
    value: "abn",
    label: "ABN",
    targetFieldName: "abn",
    businessValueGetter: (business: Business | null) =>
      business?.abn ?? UNKNOWN_VALUE,
    sourceFieldSuggestions: ["abn"],
    validation: ABNValidator,
  },

  // Enumerated fields - PICs
  ...propertyConfigurations,

  // Enumerated - Contacts
  ...contactConfigurations,
};
